import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  Settings,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotifications } from '../hooks/useNotifications';
import type { Notification } from '../types';
import { getNotificationTarget } from '../types';

// Zeit formatieren
const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

type FilterType = 'all' | 'unread';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (target: string) => void;
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onNavigate,
}: NotificationItemProps) {
  const target = getNotificationTarget(notification);
  const isExternalLink = target?.startsWith('http://') || target?.startsWith('https://');

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    if (target) {
      if (isExternalLink) {
        window.open(target, '_blank', 'noopener,noreferrer');
      } else {
        onNavigate(target);
      }
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'glass-effect rounded-[15px] p-4 cursor-pointer transition-colors hover:bg-white/15',
        !notification.is_read && 'border-l-4 border-l-pp-orange'
      )}
    >
      {/* Content */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'text-sm',
              !notification.is_read
                ? 'font-semibold text-white'
                : 'font-medium text-white/80'
            )}
          >
            {notification.title}
          </p>
          <p className="text-sm text-white/60 mt-0.5">
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <p className="text-xs text-white/40">
              {formatTime(notification.created_at)}
            </p>
            {isExternalLink && (
              <span className="inline-flex items-center gap-1 text-xs text-white/40">
                <ExternalLink className="w-3 h-3" />
                <span>Externer Link</span>
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {!notification.is_read && (
            <button
              onClick={() => onMarkAsRead(notification.id)}
              className="p-2 text-white/40 hover:text-pp-teal hover:bg-white/10 rounded-lg transition-colors"
              title="Als gelesen markieren"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onDelete(notification.id)}
            className="p-2 text-white/40 hover:text-red-400 hover:bg-white/10 rounded-lg transition-colors"
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface NotificationGroupProps {
  title: string;
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (target: string) => void;
}

function NotificationGroup({
  title,
  notifications,
  onMarkAsRead,
  onDelete,
  onNavigate,
}: NotificationGroupProps) {
  if (notifications.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="px-1 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
        {title}
      </h3>
      <div className="space-y-3">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkAsRead={onMarkAsRead}
            onDelete={onDelete}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>('all');
  const {
    notifications,
    groupedNotifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  // Gefilterte Notifications
  const filteredNotifications =
    filter === 'unread'
      ? notifications.filter((n) => !n.is_read)
      : notifications;

  // Gruppierte gefilterte Notifications
  const filteredGroups =
    filter === 'unread'
      ? {
          today: groupedNotifications.today.filter((n) => !n.is_read),
          yesterday: groupedNotifications.yesterday.filter((n) => !n.is_read),
          thisWeek: groupedNotifications.thisWeek.filter((n) => !n.is_read),
          older: groupedNotifications.older.filter((n) => !n.is_read),
        }
      : groupedNotifications;

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleNavigate = (target: string) => {
    navigate(target);
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-white">
            Mitteilungen
          </h1>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-pp-orange text-white rounded-full">
              {unreadCount} neu
            </span>
          )}
        </div>
        <Link
          to="/profile/notifications"
          className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          title="Einstellungen"
        >
          <Settings className="w-5 h-5" />
        </Link>
      </div>

      {/* Filter Tabs - Matches style */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide mb-6">
        <button
          onClick={() => setFilter('all')}
          className={cn(
            'filter-tab',
            filter === 'all' && 'active'
          )}
        >
          Alle
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={cn(
            'filter-tab',
            filter === 'unread' && 'active'
          )}
        >
          Ungelesen
        </button>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="filter-tab ml-auto flex items-center gap-1.5"
          >
            <CheckCheck className="w-4 h-4" />
            Alle gelesen
          </button>
        )}
      </div>

      {/* Content */}
      <div className="pb-24">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="w-16 h-16 glass-effect rounded-full flex items-center justify-center mb-4">
              <Bell className="w-8 h-8 text-white/40" />
            </div>
            <h2 className="text-lg font-medium text-white mb-1">
              {filter === 'unread'
                ? 'Keine ungelesenen Mitteilungen'
                : 'Keine Mitteilungen'}
            </h2>
            <p className="text-white/60 text-center">
              {filter === 'unread'
                ? 'Du hast alle Mitteilungen gelesen'
                : 'Hier erscheinen deine Benachrichtigungen'}
            </p>
          </div>
        ) : (
          <div>
            <NotificationGroup
              title="Heute"
              notifications={filteredGroups.today}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
              onNavigate={handleNavigate}
            />
            <NotificationGroup
              title="Gestern"
              notifications={filteredGroups.yesterday}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
              onNavigate={handleNavigate}
            />
            <NotificationGroup
              title="Diese Woche"
              notifications={filteredGroups.thisWeek}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
              onNavigate={handleNavigate}
            />
            <NotificationGroup
              title="Älter"
              notifications={filteredGroups.older}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
              onNavigate={handleNavigate}
            />
          </div>
        )}
      </div>
    </>
  );
}
